import { useState, useEffect, useRef } from 'react';
import { Layout } from '../components/Layout';
import axios from 'axios';
import { Progress } from '@/components/ui/progress';
import { scrollToElementWithOffset } from '../lib/scroll';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TENANCY_SITUATIONS = [
  { value: 'existing', label: 'I have current tenants', hint: 'Use this for existing pre-May 2026 tenancies.' },
  { value: 'new', label: 'Preparing for a new tenancy', hint: 'Use this for a new post-May 2026 tenancy setup.' },
  { value: 'both', label: 'Both — some let, some vacant', hint: 'Use this if you need both existing pre-May and new post-May questions.' },
];

const TENANCY_CONTEXT_LABELS = {
  existing: 'current tenancies',
  new: 'new tenancies',
};

export const LandlordHealthCheck = () => {
  const resultsRef = useRef(null);

  useEffect(() => { document.title = 'Landlord Health Check - SIYAK'; }, []);

  const [tenancySituation, setTenancySituation] = useState(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [sections, setSections] = useState([]);
  const [rulesLoaded, setRulesLoaded] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [resultsUnlocked, setResultsUnlocked] = useState(false);
  const [tosAcceptedAt, setTosAcceptedAt] = useState(null);
  const [copyStatus, setCopyStatus] = useState('');
  const [openResolutionId, setOpenResolutionId] = useState(null);

  useEffect(() => {
    if (tenancySituation === null) {
      setSections([]);
      setRulesLoaded(true);
      return;
    }

    const load = async () => {
      setRulesLoaded(false);
      try {
        const res = await axios.get(`${API_URL}/api/health-check/rules`, {
          params: { tenancy_situation: tenancySituation },
        });
        const fetched = Array.isArray(res.data?.sections) ? res.data.sections : [];
        setSections(fetched);
      } catch (err) {
        setSections([]);
      } finally {
        setRulesLoaded(true);
      }
    };
    load();
  }, [tenancySituation]);

  const ALL_QUESTIONS = sections.flatMap((s) => (s.questions || [])
    .map((q) => ({
      ...q,
      sectionId: s.id,
      sectionLabel: s.label,
      sectionAllowNotApplicable: Boolean(s.allowNotApplicable),
    })));

  const currentQuestion = ALL_QUESTIONS[step];
  const isResultsStep = rulesLoaded && ALL_QUESTIONS.length > 0 && step >= ALL_QUESTIONS.length;
  const hasCurrentAnswer = currentQuestion ? Object.prototype.hasOwnProperty.call(answers, currentQuestion.id) : false;
  const progressValue = ALL_QUESTIONS.length > 0 ? Math.round(((Math.min(step, ALL_QUESTIONS.length - 1) + 1) / ALL_QUESTIONS.length) * 100) : 0;

  const scorableAnswers = ALL_QUESTIONS.filter((q) => answers[q.id] !== 'na');
  const passed = scorableAnswers.filter((q) => answers[q.id] === true).length;
  const total = scorableAnswers.length;
  const pct = total > 0 ? Math.round((passed / total) * 100) : 0;
  const grade = pct === 100 ? 'A' : pct >= 80 ? 'B' : pct >= 60 ? 'C' : pct >= 40 ? 'D' : 'F';
  const passedQuestions = ALL_QUESTIONS.filter((q) => answers[q.id] === true);
  const actionNeededQuestions = ALL_QUESTIONS.filter((q) => answers[q.id] === false);
  const skippedQuestions = ALL_QUESTIONS.filter((q) => answers[q.id] === 'na');

  useEffect(() => {
    if (isResultsStep && resultsUnlocked && resultsRef.current) {
      scrollToElementWithOffset(resultsRef.current);
    }
  }, [isResultsStep, resultsUnlocked]);

  const getAnswerButtonClass = (value) => {
    const isSelected = currentQuestion && answers[currentQuestion.id] === value;

    if (value === true) {
      return `w-full p-3 rounded-xl border text-left transition-colors ${isSelected ? 'bg-green-100 border-green-500 text-green-900 ring-2 ring-green-200' : 'bg-green-50 border-green-200 hover:bg-green-100 cursor-pointer'}`;
    }

    if (value === false) {
      return `w-full p-3 rounded-xl border text-left transition-colors ${isSelected ? 'bg-red-100 border-red-500 text-red-900 ring-2 ring-red-200' : 'bg-red-50 border-red-200 hover:bg-red-100 cursor-pointer'}`;
    }

    return `w-full p-3 rounded-xl border text-left transition-colors ${isSelected ? 'bg-royal-50 border-royal-500 text-royal-800 ring-2 ring-royal-100' : 'border-gray-200 hover:bg-gray-50 cursor-pointer'}`;
  };

  const handleAnswer = (id, value) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
  };

  const getNotApplicableLabel = (question) => {
    if (!question) return null;

    if (question.allowNotApplicable && question.notApplicableLabel) {
      return question.notApplicableLabel;
    }

    if (tenancySituation === 'both' && question.tenancyContext) {
      return `Not applicable — this does not apply to my ${TENANCY_CONTEXT_LABELS[question.tenancyContext] || 'setup'}`;
    }

    if (question.sectionId === 'hmo' || question.sectionAllowNotApplicable) {
      return "Not applicable — I do not own an HMO";
    }

    return null;
  };

  const notApplicableLabel = getNotApplicableLabel(currentQuestion);

  const copySummary = async () => {
    const formatQuestionList = (title, questions) => {
      if (!questions.length) {
        return `${title}: None`;
      }

      return [
        title,
        ...questions.flatMap((question) => {
          const guidanceLine = question.actionUrl
            ? [`  Guidance: ${question.actionLabel || 'View guidance'} — ${question.actionUrl}`]
            : [];
          const easyAlternative = (question.alternative_step || question.siyak_step) && (question.alternative_action || question.siyak_action)
            ? [
                `  Easy alternative: ${question.alternative_step || question.siyak_step}`,
                `  Link: ${(question.alternative_action || question.siyak_action).url}`,
              ]
            : [];
          const steps = Array.isArray(question.steps)
            ? question.steps.map((stepText, index) => `  ${index + 1}. ${stepText}`)
            : [];

          return [
            `- ${question.text}`,
            ...steps,
            ...guidanceLine,
            ...easyAlternative,
          ];
        }),
      ].join('\n');
    };

    const summary = [
      'Landlord Health Check Summary',
      `Grade: ${grade}`,
      `Score: ${pct}%`,
      `Passed: ${passed} of ${total}`,
      '',
      formatQuestionList('Needs attention', actionNeededQuestions),
      '',
      formatQuestionList('Already in place', passedQuestions),
      '',
      formatQuestionList('Not applicable', skippedQuestions),
    ].join('\n');

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(summary);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = summary;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }

      setCopyStatus('Summary copied.');
    } catch (err) {
      setCopyStatus('Unable to copy summary right now.');
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 py-10">
        <h1 className="text-3xl font-bold tracking-tight">Landlord Health Check</h1>
        <p className="text-sm text-muted-foreground">Review the essentials of your letting setup with a guided landlord responsibilities and readiness check.</p>

        {/* Screener */}
        {tenancySituation === null ? (
          <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-8 max-w-2xl mx-auto mt-6">
            <h2 className="text-xl font-semibold mb-2">What best describes your situation?</h2>
            <p className="text-sm text-muted-foreground mb-4">Choose the option that best matches your current tenancy position to begin the check.</p>
            <div className="flex flex-col gap-3">
              {TENANCY_SITUATIONS.map((opt) => (
                <button key={opt.value} onClick={() => setTenancySituation(opt.value)} className="w-full p-4 rounded-xl border border-gray-200 text-left">
                  <div className="font-medium text-gray-900">{opt.label}</div>
                  <div className="mt-1 text-sm text-muted-foreground">{opt.hint}</div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-6">
            {!rulesLoaded ? (
              <div className="text-sm text-muted-foreground">Loading questions…</div>
            ) : ALL_QUESTIONS.length === 0 ? (
              <div className="text-sm text-muted-foreground">No questions available.</div>
            ) : !isResultsStep ? (
              <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6 max-w-2xl mx-auto">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-xs text-muted-foreground">{currentQuestion?.sectionLabel}</div>
                  <div className="text-xs text-muted-foreground">{step + 1} of {ALL_QUESTIONS.length}</div>
                </div>
                <Progress value={progressValue} className="mb-4 h-2 bg-royal-100 [&>*]:bg-royal-600" />
                <h3 className="text-lg font-semibold mb-4">{currentQuestion?.text}</h3>
                <div className="space-y-3">
                  <button type="button" onClick={() => handleAnswer(currentQuestion.id, true)} className={getAnswerButtonClass(true)}>Yes</button>
                  <button type="button" onClick={() => handleAnswer(currentQuestion.id, false)} className={getAnswerButtonClass(false)}>No</button>
                  {notApplicableLabel && (
                    <button type="button" onClick={() => handleAnswer(currentQuestion.id, 'na')} className={getAnswerButtonClass('na')}>{notApplicableLabel}</button>
                  )}
                </div>

                <div className="mt-4 flex gap-3">
                  {step > 0 && (
                    <button type="button" onClick={() => setStep(Math.max(0, step - 1))} className="px-4 py-2 rounded border">Back</button>
                  )}
                  {hasCurrentAnswer && (
                    <button type="button" onClick={() => setStep(Math.min(ALL_QUESTIONS.length, step + 1))} className="px-4 py-2 rounded bg-royal-600 text-white">{step < ALL_QUESTIONS.length - 1 ? 'Next' : 'See results'}</button>
                  )}
                </div>
              </div>
            ) : null
            }
            <div className="mt-3">
              <button onClick={() => {
                setTenancySituation(null);
                setStep(0);
                setAnswers({});
                setDisclaimerAccepted(false);
                setResultsUnlocked(false);
                setTosAcceptedAt(null);
                setCopyStatus('');
                setOpenResolutionId(null);
              }} className="text-royal-600">Change tenancy</button>
            </div>
          </div>
        )}

        {/* Results */}
        {isResultsStep && (
          <div ref={resultsRef} className="mt-6 max-w-2xl mx-auto space-y-4">
            {!resultsUnlocked ? (
              <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6">
                <p className="text-sm text-muted-foreground mb-3">This report is informational only and not legal advice.</p>
                <label className="flex items-start gap-2">
                  <input type="checkbox" checked={disclaimerAccepted} onChange={(e) => {
                    setDisclaimerAccepted(e.target.checked);
                    setResultsUnlocked(false);
                    if (e.target.checked) {
                      setTosAcceptedAt(new Date().toISOString());
                    }
                  }} className="mt-1" />
                  <span>
                    I understand this is informational only and I accept the{' '}
                    <a href="/terms" target="_blank" rel="noreferrer" className="text-royal-600 hover:underline">
                      Terms
                    </a>
                    .
                  </span>
                </label>
                <div className="mt-4">
                  <button disabled={!disclaimerAccepted} onClick={() => setResultsUnlocked(true)} className="px-4 py-2 rounded bg-royal-600 text-white disabled:opacity-50 disabled:cursor-not-allowed">Show my results</button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-royal-600 text-white flex items-center justify-center text-2xl font-bold">{grade}</div>
                    <div>
                      <div className="text-lg font-semibold">{pct}% — {passed} of {total} checks passed</div>
                      <div className="text-sm text-muted-foreground">{total === 0 ? 'No scored questions.' : 'This is an informational readiness check.'}</div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6 space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold">Your results</h3>
                    <p className="text-sm text-muted-foreground">Here is a quick summary of what is in place, what still needs attention, and what you marked as not applicable.</p>
                  </div>

                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="rounded-xl bg-green-50 border border-green-200 p-4">
                      <div className="text-sm text-green-700">Passed</div>
                      <div className="text-2xl font-semibold text-green-900">{passedQuestions.length}</div>
                    </div>
                    <div className="rounded-xl bg-red-50 border border-red-200 p-4">
                      <div className="text-sm text-red-700">Action needed</div>
                      <div className="text-2xl font-semibold text-red-900">{actionNeededQuestions.length}</div>
                    </div>
                    <div className="rounded-xl bg-gray-50 border border-gray-200 p-4">
                      <div className="text-sm text-gray-700">Not applicable</div>
                      <div className="text-2xl font-semibold text-gray-900">{skippedQuestions.length}</div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {actionNeededQuestions.length > 0 && (
                      <div>
                        <div className="text-sm font-medium text-red-700 mb-2">Needs attention</div>
                        <ul className="space-y-3 text-sm text-gray-700">
                          {actionNeededQuestions.map((question) => (
                            <li key={question.id} className="rounded-2xl border border-red-200 bg-white p-4 shadow-sm">
                              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                <div>
                                  <div className="font-medium text-gray-900">{question.text}</div>
                                  <div className="mt-2 flex flex-wrap items-center gap-2">
                                    <span className="inline-flex items-center rounded-full border border-red-200 bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800">
                                      Action needed
                                    </span>
                                  </div>
                                </div>
                              </div>

                              <div className="mt-2">
                                <button
                                  type="button"
                                  onClick={() => setOpenResolutionId((prev) => (prev === question.id ? null : question.id))}
                                  className="flex items-center gap-2 text-sm font-medium text-royal-600 hover:text-royal-800 transition-colors"
                                >
                                  {openResolutionId === question.id ? '▼ Hide steps' : '▶ Show me how to fix this'}
                                </button>

                                {openResolutionId === question.id && Array.isArray(question.steps) && question.steps.length > 0 && (
                                  <div className="mt-3 bg-blue-50 border border-blue-100 rounded-xl p-4">
                                    <ol className="space-y-2">
                                      {question.steps.map((stepText, index) => (
                                        <li key={`${question.id}-step-${index}`} className="flex gap-3 text-sm text-navy-800">
                                          <span className="flex-shrink-0 w-5 h-5 bg-royal-600 text-white rounded-full flex items-center justify-center text-xs font-bold mt-0.5">
                                            {index + 1}
                                          </span>
                                          <span>{stepText}</span>
                                        </li>
                                      ))}
                                    </ol>
                                    {question.actionUrl && (
                                      <a
                                        href={question.actionUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-1 mt-4 text-sm font-medium text-royal-600 hover:underline"
                                      >
                                        {question.actionLabel || 'Guidance'} ↗
                                      </a>
                                    )}
                                    {(question.alternative_step || question.siyak_step) && (question.alternative_action || question.siyak_action) && (
                                      <div className="mt-4 border-t border-blue-200 pt-3">
                                        <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1">Easy alternative</p>
                                        <p className="text-sm text-navy-800 mb-2">{question.alternative_step || question.siyak_step}</p>
                                        <a
                                          href={(question.alternative_action || question.siyak_action).url}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="inline-flex items-center gap-1 text-sm font-medium text-emerald-700 hover:underline border border-emerald-200 bg-emerald-50 rounded-lg px-3 py-1.5"
                                        >
                                          🚀 {(question.alternative_action || question.siyak_action).label} ↗
                                        </a>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {passedQuestions.length > 0 && (
                      <div>
                        <div className="text-sm font-medium text-green-700 mb-2">Already in place</div>
                        <ul className="space-y-2 text-sm text-gray-700">
                          {passedQuestions.map((question) => (
                            <li key={question.id} className="rounded-lg border border-green-100 bg-green-50 px-3 py-2">{question.text}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {skippedQuestions.length > 0 && (
                      <div>
                        <div className="text-sm font-medium text-gray-700 mb-2">Marked not applicable</div>
                        <ul className="space-y-2 text-sm text-gray-700">
                          {skippedQuestions.map((question) => (
                            <li key={question.id} className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">{question.text}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6">
                  <p className="text-sm text-muted-foreground">You can email these results to yourself or someone else.</p>
                  <div className="mt-3">
                    <label className="block text-sm font-medium">Email address for results</label>
                    <input type="email" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} placeholder="you@example.com" className="mt-2 w-full rounded-xl border px-3 py-2" />
                  </div>
                  <div className="mt-4 flex gap-3">
                    <button onClick={async () => {
                      setEmailSending(true); setEmailError('');
                      try {
                        const payload = { results: answers, recipient_email: recipientEmail || undefined };
                        const tosVersion = process.env.REACT_APP_TOS_CURRENT_VERSION || 'v1.1';
                        if (disclaimerAccepted) {
                          payload.tos_version_accepted = tosVersion;
                          payload.tos_accepted_at = tosAcceptedAt || new Date().toISOString();
                        }
                        await axios.post(`${API_URL}/api/landlord/health-check-results/email`, payload, { withCredentials: true });
                        setEmailSent(true);
                      } catch (err) {
                        setEmailError(err?.response?.data?.detail || 'Unable to send email right now.');
                      } finally {
                        setEmailSending(false);
                      }
                    }} disabled={!recipientEmail || emailSending || emailSent} className="px-4 py-2 rounded bg-royal-600 text-white">{emailSending ? 'Sending…' : emailSent ? 'Sent ✓' : 'Email my results'}</button>
                    <button type="button" onClick={copySummary} className="hidden px-4 py-2 rounded border">Copy summary</button>
                  </div>
                  {copyStatus && <div className={`text-sm mt-2 ${copyStatus === 'Summary copied.' ? 'text-green-600' : 'text-red-600'}`}>{copyStatus}</div>}
                  {emailError && <div className="text-sm text-red-600 mt-2">{emailError}</div>}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};
